import React, { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faExclamationTriangle,
  faLock,
  faSignOutAlt,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import { IAppState } from "../../interfaces";
import {
  setImitateAllowWriteAction,
  stopImpersonation,
} from "../../store/actions/authActions";
import { ETranslation } from "../../translations/translation-keys";
import ModalContext, { EModalSize } from "../UI/Modal/ModalContext";
import ConfirmDialog from "../UI/ConfirmDialog/ConfirmDialog";
import classes from "./ImpersonationBanner.module.scss";

const ImpersonationBanner: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setModal, closeModal } = useContext(ModalContext);

  const authUser = useSelector((state: IAppState) => state.auth.authUser);
  const realUser = useSelector((state: IAppState) => state.auth.realUser);
  const imitateSession = useSelector(
    (state: IAppState) => state.auth.imitateSession
  );

  const adminUser = realUser || authUser;
  if (!adminUser || !adminUser.isPrimary || !imitateSession) {
    return null;
  }

  const isWriteMode = Boolean(imitateSession.allowWrite);

  const handleExit = () => {
    dispatch(stopImpersonation());
    window.location.href = "/";
  };

  const handleEnableWriteConfirm = () => {
    dispatch(setImitateAllowWriteAction(true));
    closeModal();
  };

  const openEnableWriteModal = () => {
    setModal({
      isOpen: true,
      size: EModalSize.SMALL,
      title: t(ETranslation.IMPERSONATE_CONFIRM_WRITE_TITLE),
      content: (
        <ConfirmDialog
          body={t(ETranslation.IMPERSONATE_CONFIRM_WRITE_BODY)}
          onConfirm={handleEnableWriteConfirm}
          onCancel={closeModal}
        />
      ),
    });
  };

  const handleDisableWrite = () => {
    dispatch(setImitateAllowWriteAction(false));
  };

  const targetDisplayName =
    imitateSession.userName ||
    imitateSession.orgName ||
    `ID: ${imitateSession.userId || imitateSession.orgId}`;

  return (
    <div
      className={`${classes.Banner} ${
        isWriteMode ? classes.WriteMode : classes.SafeMode
      }`}
    >
      <div className={classes.LeftSection}>
        <span
          className={`${classes.Badge} ${
            isWriteMode ? classes.WriteBadge : classes.SafeBadge
          }`}
        >
          <FontAwesomeIcon
            icon={isWriteMode ? faExclamationTriangle : faEye}
          />
          {isWriteMode
            ? t(ETranslation.IMPERSONATE_MODE_WRITE)
            : t(ETranslation.IMPERSONATE_MODE_READONLY)}
        </span>

        <span className={classes.TargetInfo}>
          {t(ETranslation.IMPERSONATE_ACTING_AS)}:{" "}
          <strong>{targetDisplayName}</strong>
          {imitateSession.orgName && imitateSession.userName && (
            <span>({imitateSession.orgName})</span>
          )}
          <span>[{imitateSession.level}]</span>
        </span>
      </div>

      <div className={classes.RightSection}>
        {isWriteMode ? (
          <button
            type="button"
            className={`${classes.Button} ${classes.DisableWriteButton}`}
            onClick={handleDisableWrite}
          >
            <FontAwesomeIcon icon={faLock} />
            {t(ETranslation.IMPERSONATE_DISABLE_WRITE_BTN)}
          </button>
        ) : (
          <button
            type="button"
            className={`${classes.Button} ${classes.EnableWriteButton}`}
            onClick={openEnableWriteModal}
          >
            <FontAwesomeIcon icon={faEdit} />
            {t(ETranslation.IMPERSONATE_ENABLE_WRITE_BTN)}
          </button>
        )}

        <button
          type="button"
          className={`${classes.Button} ${classes.ExitButton}`}
          onClick={handleExit}
        >
          <FontAwesomeIcon icon={faSignOutAlt} />
          {t(ETranslation.IMPERSONATE_EXIT_BTN)}
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
